import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

/** Fixed right-rail width for security score, incidents, and activity cards. */
export const SECURITY_DASHBOARD_RAIL_WIDTH = 300;

const securityDashboardRailWidth = `${SECURITY_DASHBOARD_RAIL_WIDTH}px`;

/** Responsive grid columns for Security dashboard pages (sidebar + agent bar reduce usable width). */
export const useSecurityDashboardGrid = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const isLgDown = useMediaQuery(theme.breakpoints.down('lg'));
  return {
    isSmDown,
    isMdDown,
    isLgDown,
    /** Stack date selector and refresh on narrow viewports. */
    toolbarStack: isSmDown,
    /** Single column for side-by-side evolution charts. */
    dualChartColumns: isMdDown
      ? 'minmax(0, 1fr)'
      : 'repeat(2, minmax(0, 1fr))',
    /** Summary stat cards. */
    summaryColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
    /**
     * Donut cards: require ~280px per column so Apex legend+chart fit.
     * auto-fit wraps to 2–3 columns instead of squeezing 4 into a narrow main area.
     */
    quadChartColumns: isSmDown
      ? 'minmax(0, 1fr)'
      : 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
    /** Threat hunting: agents donut + wide evolution bar. */
    splitChartColumns: isLgDown
      ? 'minmax(0, 1fr)'
      : 'minmax(0, 1fr) minmax(0, 1.7fr)',
    /** Threat hunting: alert evolution + MITRE donut. */
    primaryChartColumns: isMdDown
      ? 'minmax(0, 1fr)'
      : 'minmax(0, 1.5fr) minmax(0, 1fr)',
    /** Reseller dashboard: main column + right rail. */
    mainWithRailColumns: isLgDown
      ? 'minmax(0, 1fr)'
      : `minmax(0, 1fr) ${securityDashboardRailWidth}`,
    /** Reseller dashboard: three chart cards in a row. */
    tripleChartColumns: isMdDown
      ? 'minmax(0, 1fr)'
      : 'repeat(3, minmax(0, 1fr))',
    /** Portfolio health KPI row. */
    portfolioKpiColumns: isSmDown
      ? 'minmax(0, 1fr)'
      : isMdDown
        ? 'repeat(2, minmax(0, 1fr))'
        : 'repeat(4, minmax(0, 1fr))',
    /** Portfolio health card + security score side by side. */
    portfolioHealthRowColumns: isMdDown
      ? 'minmax(0, 1fr)'
      : `minmax(0, 1fr) ${securityDashboardRailWidth}`,
    /** Overview main grid: desktop 3-col rail; tablet 2-col; mobile 1-col. */
    overviewMainColumns: isMdDown
      ? 'minmax(0, 1fr)'
      : isLgDown
        ? 'repeat(2, minmax(0, 1fr))'
        : `minmax(0, 1fr) minmax(0, 1fr) ${securityDashboardRailWidth}`,
    /** Overview stacked: one table per row (smallest breakpoints). */
    isOverviewStacked: isMdDown,
    /** Overview tablet: two tables per row, then activity full width. */
    isOverviewTablet: isLgDown && !isMdDown,
  };
};
